/**
 * Account setting: share my Synthase sessions to help improve Molecule.
 *
 * ON by default (owner decision 2026-09-24). Turning it off is stored on the
 * user row (`GET/PATCH /account/session-sharing`) and is the gate for the
 * product-review reader (`api/src/ai/refine/extract.ts` never reads a
 * conversation whose owner or writing teammate turned it off). Registered
 * accounts only — the API refuses a guest, and this section renders nothing
 * for one.
 */
import { type JSX, useEffect, useState } from 'react'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Flex, Switch } from '@molecule/app-ui-react'

/** Server response for the setting. */
interface SessionSharingState {
  shareSessions: boolean
  changedAt: string | null
  noticeDismissedAt: string | null
}

/**
 * The session-sharing switch and its one-line explanation.
 *
 * @returns The row, or null for a guest or while the choice is loading.
 */
export function AccountSessionSharingSection(): JSX.Element | null {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  const [state, setState] = useState<SessionSharingState | null>(null)
  const [hidden, setHidden] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    http
      .get<SessionSharingState>('/account/session-sharing')
      .then((res) => {
        if (!cancelled) setState(res.data)
      })
      .catch((error: unknown) => {
        // A guest (403) or a signed-out session has no choice to make here.
        if (cancelled) return
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status === 401 || status === 403) setHidden(true)
        else setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [http])

  if (hidden) return null

  const toggle = (next: boolean): void => {
    const previous = state
    setBusy(true)
    setFailed(false)
    setState((s) => ({
      shareSessions: next,
      changedAt: s?.changedAt ?? null,
      noticeDismissedAt: s?.noticeDismissedAt ?? null,
    }))
    http
      .patch<SessionSharingState>('/account/session-sharing', { shareSessions: next })
      .then((res) => setState(res.data))
      .catch(() => {
        setState(previous)
        setFailed(true)
      })
      .finally(() => setBusy(false))
  }

  const label = t('settings.sessionSharing.label', undefined, {
    defaultValue: 'Share my sessions to help improve Molecule',
  })

  return (
    <div data-mol-id="settings-session-sharing">
      <Flex align="center" justify="between" gap="sm">
        <span className={cm.textSize('sm')}>{label}</span>
        <Switch
          checked={state?.shareSessions ?? true}
          onChange={(e) => toggle((e.target as HTMLInputElement).checked)}
          disabled={busy || state === null}
          size="sm"
          aria-label={label}
          data-mol-id="settings-session-sharing-toggle"
        />
      </Flex>
      <p className={cm.cn(cm.textSize('xs'), cm.textMuted, cm.sp('mt', 1))}>
        {t('settings.sessionSharing.desc', undefined, {
          defaultValue:
            'Our team and automated reviews read your Synthase conversations to find what Molecule is missing and fix problems you run into. On by default. Turn it off anytime.',
        })}
      </p>
      {failed && (
        <p className={cm.cn(cm.textSize('xs'), cm.textError, cm.sp('mt', 1))}>
          {t('settings.sessionSharing.failed', undefined, {
            defaultValue: 'Could not save this setting. Try again.',
          })}
        </p>
      )}
    </div>
  )
}
